import React, { useEffect, useState, useCallback, useContext } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  Card,
  CardMedia,
  CardContent,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ImageIcon from "@mui/icons-material/Image";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import HouseIcon from "@mui/icons-material/House";
import DocumentViewerDialog from "./DocumentViewerDialog";

const PRIMARY = "#6366f1";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// Styled wrapper for Quill editor with custom MUI styling
const StyledQuillWrapper = styled(Box)(({ theme }) => ({
  "& .ql-toolbar.ql-snow": {
    border: "1px solid #e0e0e0",
    borderBottom: "none",
    backgroundColor: "#fafafa",
    borderRadius: "4px 4px 0 0",
    padding: "8px 12px",
  },
  "& .ql-container.ql-snow": {
    border: "1px solid #e0e0e0",
    borderRadius: "0 0 4px 4px",
    fontFamily: theme.typography.fontFamily,
  },
  "& .ql-editor": {
    minHeight: "400px",
    fontSize: "14px",
    lineHeight: "1.6",
    padding: "16px",
    "&.ql-blank::before": {
      color: "#9e9e9e",
      fontStyle: "italic",
      fontSize: "14px",
    },
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#ccc",
      borderRadius: "4px",
      "&:hover": {
        background: "#999",
      },
    },
    "& h1": {
      fontSize: "2em",
      fontWeight: "bold",
      margin: "0.67em 0",
    },
    "& h2": {
      fontSize: "1.5em",
      fontWeight: "bold",
      margin: "0.75em 0",
    },
    "& h3": {
      fontSize: "1.17em",
      fontWeight: "bold",
      margin: "0.83em 0",
    },
    "& p": {
      margin: "0.5em 0",
    },
    "& ul, & ol": {
      paddingLeft: "1.5em",
    },
    "& li": {
      margin: "0.25em 0",
    },
  },
  "& .ql-toolbar button": {
    width: "28px !important",
    height: "28px !important",
    padding: "3px !important",
    "&:hover, &:focus": {
      color: `${PRIMARY} !important`,
      "& .ql-stroke": {
        stroke: `${PRIMARY} !important`,
      },
      "& .ql-fill": {
        fill: `${PRIMARY} !important`,
      },
    },
    "&.ql-active": {
      color: `${PRIMARY} !important`,
      "& .ql-stroke": {
        stroke: `${PRIMARY} !important`,
      },
      "& .ql-fill": {
        fill: `${PRIMARY} !important`,
      },
    },
  },
  "& .ql-stroke": {
    stroke: "#666 !important",
  },
  "& .ql-fill": {
    fill: "#666 !important",
  },
  "& .ql-picker-label": {
    color: "#666 !important",
    "&:hover": {
      color: `${PRIMARY} !important`,
    },
  },
  "& .ql-picker-options": {
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  "& .ql-picker-item": {
    "&:hover": {
      backgroundColor: "#f5f5f5",
      color: PRIMARY,
    },
  },
}));

const initialItineraryState = {
  name: "",
  description: "",
  total_days: 1,
  content: "", // This will store the rich text HTML
  images: [],
  customers: [],
};

// Quill toolbar configuration with comprehensive formatting options
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["blockquote", "code-block"],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "align",
  "blockquote",
  "code-block",
  "link",
  "image",
  "video",
];

const ItineraryMaster = () => {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const navigate = useNavigate();
  const userID = userInfo?.id;
  const userStoreID = userInfo?.store_id;

  const [searchText, setSearchText] = useState("");
  const [selectedItineraryId, setSelectedItineraryId] = useState(null);
  const [itineraryDetail, setItineraryDetail] = useState(initialItineraryState);
  const [itineraries, setItineraries] = useState([]);
  const [isNewItinerary, setIsNewItinerary] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);

  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const closeSnackbar = () =>
    setSnackBarConfig((prev) => ({ ...prev, open: false }));
  const isEditMode = !!selectedItineraryId && !isNewItinerary;

  // Convert File to Base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAddNewItinerary = () => {
    setSelectedItineraryId(null);
    setItineraryDetail(initialItineraryState);
    setIsNewItinerary(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const handleChange = (field, value) => {
    setItineraryDetail((p) => ({ ...p, [field]: value }));
  };

  // Handle rich text editor content change
  const handleContentChange = (content) => {
    setItineraryDetail((p) => ({ ...p, content: content }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const base64 = await convertFileToBase64(file);
      setItineraryDetail((p) => ({
        ...p,
        images: [...p.images, { fileName: file.name, file: base64 }],
      }));
    }
  };

  const updateImage = (index, field, value) => {
    const updatedImages = [...itineraryDetail.images];
    updatedImages[index][field] = value;
    setItineraryDetail((p) => ({ ...p, images: updatedImages }));
  };

  const deleteImage = (index) => {
    setItineraryDetail((p) => ({
      ...p,
      images: p.images.filter((_, i) => i !== index),
    }));
  };

  const handleCancel = () => {
    setItineraryDetail(initialItineraryState);
    setSelectedItineraryId(null);
    setIsNewItinerary(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const itinerariesFetcher = useCallback(async () => {
    try {
      setIsListLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/itineraries/store/${userStoreID}`,
      );
      if (!res.data.success) throw res.data;
      setItineraries(res.data?.data ?? []);
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: error.statusMessage ?? "Failed to load itineraries",
        severity: "error",
      });
    } finally {
      setIsListLoading(false);
    }
  }, [userStoreID]);

  useEffect(() => {
    itinerariesFetcher();
  }, [itinerariesFetcher]);

  const fetchOneItinerary = async (itineraryId) => {
    try {
      setIsListLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/itineraries/${itineraryId}`,
      );
      if (!res.data.success) throw res.data;

      const itinerary = res.data.data;
      setItineraryDetail({
        name: itinerary.name || "",
        description: itinerary.description || "",
        total_days: itinerary.total_days || 1,
        content: itinerary.content || "",
        images: itinerary.images || [],
        customers: itinerary.customers || [],
      });

      setSelectedItineraryId(itineraryId);
      setIsNewItinerary(false);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: error?.statusMessage || "Failed to load itinerary data",
        severity: "error",
      });
    } finally {
      setIsListLoading(false);
    }
  };

  const deleteItinerary = async (itineraryId) => {
    try {
      setIsSaving(true);
      const res = await axios.delete(
        `${API_BASE_URL}/api/travel/itineraries/${itineraryId}`,
      );
      if (!res.data.success) throw res.data;
      setSelectedItinerary(null);
      setOpenDialog(false);
      handleCancel();
      setSnackBarConfig({
        open: true,
        message: res.data.statusMessage || "Itinerary deleted successfully!",
        severity: "success",
      });
      itinerariesFetcher();
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: error?.statusMessage || "Failed to delete itinerary",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!itineraryDetail.name.trim() || itineraryDetail.total_days < 1) {
      setSnackBarConfig({
        open: true,
        message: "Please fill in all required fields: Name and Total Days.",
        severity: "error",
      });
      return;
    }

    const payload = {
      store_id: userStoreID,
      name: itineraryDetail.name,
      description: itineraryDetail.description || null,
      total_days: itineraryDetail.total_days,
      created_by: userID,
      content: itineraryDetail.content, // Save rich text HTML
      images: itineraryDetail.images,
      customers: itineraryDetail.customers || [],
    };

    try {
      setIsSaving(true);
      let response;

      if (selectedItineraryId && !isNewItinerary) {
        response = await axios.put(
          `${API_BASE_URL}/api/travel/itineraries/${selectedItineraryId}`,
          payload,
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/travel/itineraries/create`,
          payload,
        );
      }

      if (!response.data.success) throw response.data;

      setSnackBarConfig({
        open: true,
        message: response.data.statusMessage || "Itinerary saved successfully",
        severity: "success",
      });

      itinerariesFetcher();
      setTimeout(() => {
        setItineraryDetail(initialItineraryState);
        setSelectedItineraryId(null);
        setIsNewItinerary(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 300);
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: error?.statusMessage || "Operation failed",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredItineraries = itineraries.filter(
    (item) =>
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description.toLowerCase().includes(searchText.toLowerCase()),
  );

  return (
    <Box p={2} sx={{ background: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box
          onClick={() => navigate(-1)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: 2,
            bgcolor: "#139fcae8",
            p: 0.5,
          }}
        >
          <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
        </Box>
        <Box
          onClick={() => navigate("/travel/dashboard")}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: 2,
            bgcolor: "#139fcae8",
            p: 0.5,
          }}
        >
          <HouseIcon sx={{ fill: "#FFF" }} />
        </Box>
        <MapRoundedIcon sx={{ fill: "#139fcae8" }} />
        <Typography variant="h6" fontWeight={700} sx={{ color: "#139fcae8" }}>
          Itinerary Master
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* LEFT SIDEBAR */}
        <Grid item xs={12} md={2.5}>
          <Paper
            sx={{
              p: 2,
              height: "calc(100vh - 140px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Search & Add Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,

                mb: 2,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search itineraries"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNewItinerary}
                sx={{
                  bgcolor: PRIMARY,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: PRIMARY, opacity: 0.9 },
                }}
              >
                New
              </Button>
            </Box>

            {/* All Itineraries List */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",

                // Hide scrollbar (Chrome, Safari)
                "&::-webkit-scrollbar": {
                  width: 0,
                  height: 0,
                },

                // Hide scrollbar (Firefox)
                scrollbarWidth: "none",

                // Hide scrollbar (IE/Edge)
                msOverflowStyle: "none",
              }}
            >
              <Stack
                spacing={1}
                sx={{
                  "&::-webkit-scrollbar": { width: 0, height: 0 },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {filteredItineraries.map((itinerary) => (
                  <Paper
                    key={itinerary.id}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      border:
                        selectedItineraryId === itinerary.id
                          ? `2px solid ${PRIMARY}`
                          : "1px solid #e5e7eb",
                      bgcolor:
                        selectedItineraryId === itinerary.id
                          ? `${PRIMARY}08`
                          : "#fff",
                      "&:hover": {
                        bgcolor: `${PRIMARY}05`,
                        borderColor: PRIMARY,
                      },
                    }}
                    onClick={() => fetchOneItinerary(itinerary.id)}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-end"
                      gap={1}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Tooltip title={itinerary.name}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {itinerary.name}
                            </Typography>
                          </Tooltip>

                          <Chip
                            icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
                            label={`${itinerary.total_days} ${
                              itinerary.total_days === 1 ? "day" : "days"
                            }`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              bgcolor: `${PRIMARY}15`,
                              color: PRIMARY,
                              flexShrink: 0,
                            }}
                          />
                        </Box>

                        {itinerary.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mt: 0.5,
                            }}
                          >
                            {itinerary.description}
                          </Typography>
                        )}
                      </Box>

                      {/* Delete Icon */}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedItinerary(itinerary);
                          setOpenDialog(true);
                        }}
                        sx={{ color: "#ef4444" }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}

                {filteredItineraries.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                  >
                    No itineraries found
                  </Typography>
                )}
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT CONTENT */}
        <Grid item xs={12} md={9.5}>
          <Paper sx={{ p: 2 }}>
            {/* Header with Actions */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2, pb: 1.5, borderBottom: "1px solid #e5e7eb" }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {isEditMode ? "Edit Itinerary" : "Create Itinerary"}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  sx={{ textTransform: "none", minWidth: 80 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    bgcolor: PRIMARY,
                    textTransform: "none",
                    minWidth: 80,
                    "&:hover": { bgcolor: PRIMARY, opacity: 0.9 },
                  }}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </Stack>
            </Stack>

            {/* Basic Info - Compact Layout */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={1}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  type="number"
                  label="Days"
                  value={itineraryDetail.total_days}
                  onChange={(e) =>
                    handleChange("total_days", parseInt(e.target.value) || 1)
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} sm={4.5}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Itinerary Name"
                  value={itineraryDetail.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6.5}>
                <TextField
                  fullWidth
                  multiline
                  // rows={2}
                  size="small"
                  label="Description"
                  placeholder="Brief description..."
                  value={itineraryDetail.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Rich Text Editor for Itinerary Content */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                Itinerary Content
              </Typography>
              <StyledQuillWrapper>
                <ReactQuill
                  theme="snow"
                  value={itineraryDetail.content}
                  onChange={handleContentChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter detailed day plan content here... use formatting tools above."
                />
              </StyledQuillWrapper>
            </Box>

            {/* Images - Ultra Compact */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" fontWeight={700}>
                  Gallery ({itineraryDetail.images.length})
                </Typography>
                <Button
                  size="small"
                  startIcon={<ImageIcon />}
                  component="label"
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    minWidth: 90,
                  }}
                >
                  Upload
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Stack>

              {itineraryDetail.images.length > 0 ? (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {itineraryDetail.images.map((img, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          border: "1px solid #eee",
                          overflow: "hidden",
                          transition: "0.2s",
                          "&:hover": {
                            boxShadow: 3,
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        {/* Image Preview */}
                        <CardMedia
                          component="img"
                          height="160"
                          image={img.file}
                          alt={img.fileName}
                          sx={{
                            cursor: "pointer",
                            objectFit: "cover",
                          }}
                          onClick={() =>
                            setViewerDoc({
                              name: itineraryDetail.name || "Itinerary Image",
                              number: index + 1,
                              file: img.file,
                              fileName: img.fileName,
                              renewalDate: null,
                            })
                          }
                        />

                        {/* Image Controls */}
                        <CardContent>
                          <Stack
                            spacing={1}
                            direction="row"
                            justifyContent="space-between"
                          >
                            {/* Rename Field */}
                            <TextField
                              size="small"
                              value={img.fileName}
                              onChange={(e) =>
                                updateImage(index, "fileName", e.target.value)
                              }
                              placeholder="Image name"
                              fullWidth
                            />

                            {/* Delete */}
                            <Tooltip title="Delete Image">
                              <IconButton
                                size="small"
                                onClick={() => deleteImage(index)}
                                sx={{ color: "#ef4444" }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 3,
                    border: "2px dashed #e5e7eb",
                    borderRadius: 1,
                    bgcolor: "#f9fafb",
                  }}
                >
                  <ImageIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    No images uploaded
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Image Preview Dialog */}
      <DocumentViewerDialog
        doc={viewerDoc}
        onClose={() => setViewerDoc(null)}
      />

      {/* Delete Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete itinerary "{selectedItinerary?.name}
          "?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteItinerary(selectedItinerary?.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ItineraryMaster;
